import { Dimensions, Platform } from 'react-native';
import palette from './colours.json';
import { fonts } from './typography';

// React Native
const { width: viewportWidth, height: viewportHeight } =
  Dimensions.get('window');

const guidelineBaseWidth = 350;
// const guidelineBaseHeight = 680;

const scale = (size: number) => (viewportWidth / guidelineBaseWidth) * size;

export const isWeb = Platform.OS === 'web';
export const isIos = Platform.OS === 'ios';
export const isAndroid = Platform.OS === 'android';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const moderateScale = (size: number, factor = 0.5) =>
  isWeb
    ? size * 1.4
    : isAndroid
    ? size + (scale(size) - size) * 0.35
    : size + (scale(size) - size) * 0.4;

const deviceWidth = viewportWidth;
const deviceHeight = viewportHeight;

const colors = {
  ...palette,
  gradient: `linear-gradient(to bottom right, ${palette.pink}, ${palette.deepPurple})`,
  overlayBlack: 'rgba(0, 0, 0, 0.3)',
  states: {
    positive: palette.green,
    positiveXL: palette.greenLightest,
    neutral: palette.blue,
    neutralXL: palette.blueLightest,
    focus: palette.orange,
    focusXL: palette.orangeLightest,
    negative: palette.red,
    negativeXL: palette.redLightest,
  },
};

const fontSize = {
  xs: 14,
  small: 16,
  medium: 18,
  large: 22,
  xl: 28,
};

const radius = {
  small: 4,
  medium: 6,
  large: 10,
};

const padding = {
  small: 5,
  medium: 10,
  large: 20,
  xl: 40,
};

const spacing = {
  small: 10,
  medium: 20,
  large: 40,
  xl: 60,
};

const gradient = {
  startAxis: { x: 0.0, y: 0.5 },
  endAxis: { x: 0.5, y: 1.0 },
  location: [0, 0.8],
};

const font = {
  regular: 'Inter-Regular',
  bold: 'Inter-Bold',
  medium: 'Inter-Medium',
  semibold: 'Inter-Semibold',
};

const border = {
  solid: {
    borderWidth: 1,
    borderColor: palette.grey3,
    borderStyle: 'solid',
  },
  dashed: {
    borderWidth: 1,
    borderColor: palette.grey3,
    borderStyle: 'dashed',
  },
};

const shadow = {
  light: {
    shadowColor: palette.black,
    shadowOpacity: 0.15,
    shadowOffset: { width: 0, height: 0 },
    shadowRadius: 6,
    elevation: 3,
  },
  strong: {
    shadowColor: palette.black,
    shadowOpacity: 0.3,
    shadowOffset: { width: 0, height: 0 },
    shadowRadius: 6,
    elevation: 6,
  },
};

const containers = {
  enabled: {
    backgroundColor: palette.white,
    ...border.solid,
  },
  enabledAlt: {
    backgroundColor: palette.grey1,
    ...border.solid,
  },
  active: {
    backgroundColor: palette.white,
    ...border.solid,
    ...shadow.light,
  },
  disabled: {
    backgroundColor: palette.grey1,
  },
  default: {
    backgroundColor: palette.white,
  },
  positive: {
    backgroundColor: colors.states.positiveXL,
  },
  neutral: {
    backgroundColor: colors.states.neutralXL,
  },
  focus: {
    backgroundColor: colors.states.focusXL,
  },
  negative: {
    backgroundColor: colors.states.negativeXL,
  },
  note: {
    backgroundColor: palette.amberLightest,
    borderWidth: 1,
    borderColor: palette.amber,
    borderStyle: 'solid',
  },
};

const layoutWidth = {
  s: 540,
};

const tillWhiteLogo =
  'data:image/png;base64,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';

const theme = {
  moderateScale,
  deviceWidth,
  deviceHeight,
  colors,
  fontSize,
  radius,
  spacing,
  padding,
  gradient,
  font,
  tillWhiteLogo,
  containers,
  shadow,
  fonts,
  layoutWidth,
};

export default theme;
